import * as React from 'react';
import { ReactNode, useEffect, useState } from 'react';
import { SearchRequestSort } from '../../../api/core';
import { Paginator, PaginatorPageChangeEvent, PaginatorTemplateOptions } from 'primereact/paginator';
import { pageSizeOptions } from '../../../Settings/config';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { LabelledSearchRequestSort, SolrEntity } from '../../../util/types';
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber';

export interface SearchResultsListProps {
  children?: React.ReactNode[];
  results: SolrEntity[];
  total: number;
  offset: number;
  rowsPerPage: number;
  onPageChange: (props: PaginatorPageChangeEvent) => void;
  sort: SearchRequestSort;
  sortOptions: LabelledSearchRequestSort[];
  onSortChange: (value: SearchRequestSort) => void;
  embeddedSearch: boolean;
}

export function SearchResultsList(props: SearchResultsListProps): ReactNode {
  const intl: IntlShape = useIntl();
  const totalPages: number = Math.ceil(props.total / props.rowsPerPage);
  const [page, setPage] = useState<number>( 1);

  useEffect((): void => setPage((props.offset / props.rowsPerPage) + 1), [props.offset, props.rowsPerPage]);

  const leftContent =
    <span>
      <Dropdown
        value={props.rowsPerPage}
        onChange={(e: DropdownChangeEvent): void => props.onPageChange({page: page - 1, pageCount: props.total, rows: e.value as number, first: props.offset})}
        options={pageSizeOptions}
      />
    </span>;

  const rightContent =
    <span className={'flex gap-1 align-items-center'}>
      <span>{intl.formatMessage({id: 'paginator.page'})}</span>
      <InputNumber
        value={page}
        useGrouping={false}
        onChange={(e: InputNumberChangeEvent): void => setPage(e.value <= totalPages ? e.value : totalPages)}
        size={4}
        min={1}
        step={1}
        maxFractionDigits={0}
        max={totalPages}
        inputClassName={'text-right'}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => {
          if(e.key === 'Enter') {
            props.onPageChange({page: page - 1, pageCount: props.total, rows: props.rowsPerPage, first: props.offset});
          }
        }}
      />
      <span>{intl.formatMessage({id: 'paginator.of'})}</span>
      <span>{intl.formatNumber(totalPages)}</span>
    </span>;

  const template: PaginatorTemplateOptions = { layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink' };

  const paginator = <Paginator
    template={template}
    first={props.offset}
    rows={props.rowsPerPage}
    totalRecords={props.total}
    rowsPerPageOptions={pageSizeOptions}
    onPageChange={props.onPageChange}
    leftContent={leftContent}
    rightContent={rightContent}
  />;

  return (
    <div className={'flex flex-column gap-2 px-3'}>
      <div className={'flex flex-column'}>
        <div className={'w-12 flex justify-content-end px-3 justify-content-between text-gray-500'}>
          <div>
            <span>{intl.formatNumber(props.total)}</span>
            <span>&nbsp;{intl.formatMessage({ id: 'paginator.results' })}</span>
          </div>
          <Dropdown
            className={'w-20rem h-2rem'}
            value={props.sort}
            options={props.sortOptions}
            onChange={(e: DropdownChangeEvent): void => props.onSortChange(e.value)}
            valueTemplate={(o: LabelledSearchRequestSort): ReactNode => <FormattedMessage id={o.label} />}
            itemTemplate={(o: LabelledSearchRequestSort): ReactNode => <FormattedMessage id={o.label} />}
          ></Dropdown>
        </div>
        <div>{paginator}</div>
      </div>
      <div className={'surface-100 border-round flex flex-column gap-3 p-3'}>
        {props.children.length ? props.children : intl.formatMessage({ id: 'search.no_results' })}
      </div>
      <div>{paginator}</div>
      <div className={'w-12 flex justify-content-end px-3'}>
        <Dropdown
          className={'w-20rem h-2rem'}
          value={props.sort}
          options={props.sortOptions}
          onChange={(e: DropdownChangeEvent): void => props.onSortChange(e.value as SearchRequestSort)}
          valueTemplate={(o: LabelledSearchRequestSort): ReactNode => <FormattedMessage id={o.label} />}
          itemTemplate={(o: LabelledSearchRequestSort): ReactNode => <FormattedMessage id={o.label} />}
        ></Dropdown>
      </div>
    </div>
  );
}

export default SearchResultsList;
